import { gql } from '@apollo/client';
import { validateEmail } from '@arcadiapower/warbler';
import { ValidatingInput, InputComponentProps } from '~/components/validating-input';
import { LDClient, LDFlags } from '~/providers';
import { EMAIL_REGEX } from '~/utils/constants';
import { TRACK, trackEvent } from '~/utils/tracking';
import { EmailStatus, EmailSubStatus, fetchData, validateEmailOnRegEx } from './email.util';

export const VALIDATE = gql`
  query Flex_ValidateEmail($email: String!) {
    validation {
      availableEmail(email: $email) {
        isValid
      }
    }
  }
`;
export const Email = (props: InputComponentProps) => {
  const validate = async ({ apollo, value }) => {
    if (!validateEmail(value)) return 'Please enter a valid email address';
    const { data } = await apollo.query({
      fetchPolicy: 'no-cache',
      query: VALIDATE,
      variables: { email: value },
    });
    if (!data.validation.availableEmail.isValid) {
      trackEvent(TRACK.emailAlreadyExistsResulted);
      return (
        <span>
          This email is already in use. Please <a href="https://login.arcadia.com">sign in</a>{' '}
          instead.
        </span>
      );
    }

    const inValidStatus: EmailStatus[] = ['invalid', 'do_not_mail', 'unknown'];
    const allowedSubstatus: EmailSubStatus[] = ['role_based', 'role_based_catch_all'];

    function isInValidEmail(status: string, subStatus?: string): boolean {
      if (status === 'do_not_mail') return !allowedSubstatus.includes(subStatus as EmailSubStatus);
      return inValidStatus.includes(status as EmailStatus);
    }

    function shouldUseZeroBounce(email: string): boolean {
      return (
        LDClient.variation(LDFlags.ffUseZeroBounceEmailVerification) &&
        !validateEmailOnRegEx(email, EMAIL_REGEX)
      );
    }

    const result = shouldUseZeroBounce(value)
      ? await fetchData(value.replace(/\+/g, '%2B') as string)
      : null;
    if (result && isInValidEmail(result.status, result.sub_status)) {
      trackEvent(TRACK.emailCategorizedAsInvalid, {
        email: value,
        status: result.status,
        subStatus: result.sub_status,
      });
      return (
        <span>
          {`This email is categorized as ${
            result.status === 'unknown' ? result.sub_status : result.status
          }, Please enter a valid email address.`}
        </span>
      );
    }

    return true;
  };

  return (
    <ValidatingInput
      autoComplete="username"
      label="Email"
      name="email"
      placeholder="example@arcadia.com"
      validate={validate}
      {...props}
    />
  );
};
